import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";
import "../css/Common-Styles.css";
import "../css/Common.css";
import { useUser } from "../context/UserContext";
import ConsentAPI from "../services/ConsentAPI";
import { useTranslation } from "../../../i18n/LanguageContext";
import ActivityTab from "./components/ActivityTab";
import MemberDashboardPanel from "./components/MemberDashboardPanel";
import MemberAnalysesPanel from "./components/MemberAnalysesPanel";
import MemberReportsPanel from "./components/MemberReportsPanel";

/**
 * MemberActivityView
 * Full-page Hero panel for monitoring a member's activity.
 * Launched from the User Management Actions → Monitor Activity dropdown.
 *
 * Navigation flow:
 *   User Management → [Monitor Activity] → MemberActivityView (activity)
 *                                        → MemberDashboardPanel (dashboard)
 *                                        → MemberAnalysesPanel  (analyses)
 *                                        → MemberReportsPanel   (reports)
 */
function MemberActivityView({ setHeroContent, goBack }) {
  const { selectedUser: member, loggedInUser, selectUser } = useUser();
  const { t } = useTranslation();

  // "activity" | "dashboard" | "analyses" | "reports"
  const [panel, setPanel] = useState("activity");
  const [consentStatus, setConsentStatus] = useState({
    dashboardAccess: false,
    analysisAccess: false,
    reportsAccess: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (member?.uuid) {
      loadConsent(member.uuid);
    } else {
      setLoading(false);
    }
  }, [member?.uuid]);

  const loadConsent = async (uuid) => {
    setLoading(true);
    try {
      const fresh = await ConsentAPI.checkMemberConsent(uuid);
      if (fresh) {
        setConsentStatus({
          dashboardAccess: fresh.dashboardAccess || false,
          analysisAccess: fresh.analysisAccess || false,
          reportsAccess: fresh.reportsAccess || false,
        });
      }
    } catch {
      // fall back to defaults
    } finally {
      setLoading(false);
    }
  };

  const handleBackToUserMgmt = () => {
    // Reset selected user back to the account owner's context
    if (loggedInUser) selectUser(loggedInUser);
    // Use goBack so previousContent is cleared and no back-link appears on User Management
    if (goBack) {
      goBack();
    } else {
      setHeroContent("Member User Info");
    }
  };

  const memberName = member
    ? `${member.firstName} ${member.lastName}`
    : t("usermgmt.activity.thisMember");

  // ── Sub-panel views ──────────────────────────────────────────────────────

  if (panel === "dashboard") {
    return (
      <MemberDashboardPanel
        member={member}
        onBack={() => setPanel("activity")}
      />
    );
  }

  if (panel === "analyses") {
    return (
      <MemberAnalysesPanel
        member={member}
        onBack={() => setPanel("activity")}
      />
    );
  }

  if (panel === "reports") {
    return (
      <MemberReportsPanel member={member} onBack={() => setPanel("activity")} />
    );
  }

  // ── Activity panel (default) ─────────────────────────────────────────────

  return (
    <div className="common-container">
      <div className="common-header">
        <button
          className="umv2-activity-back-btn"
          onClick={handleBackToUserMgmt}
          type="button"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t("usermgmt.activity.backToUserMgmt")}
        </button>
        <h1 className="main-title">{memberName}</h1>
        <p className="common-subtitle">
          {t("usermgmt.activity.intro").replace("{name}", memberName)}
        </p>
      </div>

      {loading ? (
        <div className="umv2-sheet-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>{t("common.loading")}</span>
        </div>
      ) : (
        <div className="umv2-section-body">
          <ActivityTab
            member={member}
            consentStatus={consentStatus}
            onViewDashboard={() => setPanel("dashboard")}
            onViewAnalyses={() => setPanel("analyses")}
            onViewReports={() => setPanel("reports")}
          />
        </div>
      )}
    </div>
  );
}

export default MemberActivityView;
